import { AppIconModel } from "client_legacy/AppIcon/common";
import {
	FormItem,
	TextInput,
	ValidationError,
	Checkbox
} from "client/Components";
import { AppColorSelectFormItem } from "client/Features/Apps/AppColorSelectFormItem";
import { AppDimensionSelectFormItem } from "client/Features/Apps/AppDimensionSelectFormItem";
import { AppIconSelector, convertColorToViewModel } from "client/Features/Apps/AppIconSelector";
import { GenericEditFormData } from "client/Features/Workspace/LeftSliders/EditTile/EditGenericTile";
import { IFormHandler } from "client/Forms";
import { ReactElement, useCallback } from "react";
import { AppColorViewModel } from "client/Api/Apps/common";
import { localize } from "client_legacy/ndaw/culture";
import { GenericDetailsTileModel } from "client/Api/Workspace/workspace";
import { IValueInputAttachable } from "client/Forms/attachable";

export type EditTileGeneralFieldsProps<TModel> = {
	initialModel: TModel;
	formHandler: IFormHandler<GenericEditFormData>;
};

export const EditTileGeneralFields = <TModel extends GenericDetailsTileModel>(
	props: EditTileGeneralFieldsProps<TModel>
): ReactElement => {
	const {
		initialModel,
		formHandler,
	} = props;

	const actualColor: AppColorViewModel | null = convertColorToViewModel(
		formHandler.reducer
			.value
			.color,
		initialModel.defaultAppColor
	);

	const imageAttachable: IValueInputAttachable<AppIconModel> = formHandler.for("image");
	const shouldHideNameHandler: IValueInputAttachable<SetOptionalModel<boolean>> = formHandler.for("shouldHideName");

	const imageChangeHandler = useCallback(
		(image: AppIconModel): void => {
			imageAttachable
				.reducer
				.setValue(image);

			imageAttachable
				.validation
				.triggerInputFinished();
		},
		[imageAttachable]
	);

	const {
		dimension
	} = formHandler
		.reducer
		.value;

	const is2x2Tile: boolean = dimension.columnSpan === 2
		&& dimension.rowSpan === 2;

	return (
		<>
			<FormItem
				label={localize("_Icon")}
			>
				<AppIconSelector
					color={actualColor}
					value={imageAttachable.value}
					onChange={imageChangeHandler}
					trackingId={imageAttachable.reducer.path}
				/>
				<ValidationError {...formHandler.under("image").attachToValidationError()}/>
			</FormItem>
			<AppColorSelectFormItem
				attachable={formHandler.for("color")}
			/>
			<AppDimensionSelectFormItem
				appType={initialModel.appType}
				attachable={formHandler.for("dimension")}
			/>
			<FormItem
				label={localize("_AppName")}
			>
				<TextInput
					{...formHandler.for("name").attachToTextInput()}
				/>
				<ValidationError {...formHandler.for("name").attachToValidationError()}/>
			</FormItem>
			{is2x2Tile && (
				<FormItem>
					<Checkbox
						{...shouldHideNameHandler.for("value").attachToCheckbox()}
						text={localize("_HideAppName")}
					/>
					<ValidationError {...shouldHideNameHandler.for("value").attachToValidationError()}/>
				</FormItem>
			)}
		</>
	);
};
EditTileGeneralFields.displayName = "EditTileGeneralFields";
